import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import ContactAvatar from "../../components/ContactAvatar";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import { toast } from "react-toastify";
import api from "../../services/api";
import sidebarScrollbar from "../../helpers/sidebarScrollbar";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ContactModal from "../../components/ContactModal";
import ContactTimeline from "../../components/ContactTimeline";
import ContactTagsEditor from "../../components/ContactTagsEditor";
import ContactAlerts from "../../components/ContactAlerts";
import useContactFieldTemplates from "../../hooks/useContactFieldTemplates";
import { splitContactFields } from "../../helpers/contactFields";
import NewTicketModal from "../../components/NewTicketModal";
import OpportunityModal from "../../components/OpportunityModal";
import TaskEditor from "../../components/TaskEditor";
import Chip from "@material-ui/core/Chip";
import AddIcon from "@material-ui/icons/Add";
import formatPhone from "../../helpers/formatPhone";
import { formatCustomerSince, getBirthdayInfo } from "../../helpers/contactDates";

// referência fixa: evita o editor de tags reiniciar a cada render
const EMPTY_TAGS = [];

// selo de aniversário quando faltam até 7 dias
const birthdayBadge = (info) => {
  if (!info || info.daysUntil > 7) return null;
  if (info.daysUntil === 0) return "🎂 Aniversário hoje!";
  if (info.daysUntil === 1) return "🎂 Aniversário amanhã";
  return `🎂 Aniversário em ${info.daysUntil} dias`;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...sidebarScrollbar(theme),
  },
  headerCard: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  avatar: {
    width: 72,
    height: 72,
  },
  info: {
    flex: 1,
    minWidth: 200,
  },
  actions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  extraInfo: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  // mesmo padrão do botão "Adicionar nota" do histórico
  actionButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    height: 40,
    padding: theme.spacing(0, 2.5),
  },
  secondaryButton: {
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
  },
  smallActionButton: {
    height: 36,
    padding: theme.spacing(0, 2),
  },
  timelinePaper: {
    padding: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
  centered: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
}));

const ContactProfile = () => {
  const classes = useStyles();
  const history = useHistory();
  const { contactId } = useParams();

  const [contact, setContact] = useState(null);
  const [events, setEvents] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [opportunityModalOpen, setOpportunityModalOpen] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState(null);
  const [taskOpen, setTaskOpen] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [{ data: contactData }, { data: timelineData }, { data: oppData }] =
        await Promise.all([
          api.get(`/contacts/${contactId}`),
          api.get(`/contacts/${contactId}/timeline`),
          api.get("/opportunities", { params: { contactId } }),
        ]);
      setContact(contactData);
      setEvents(timelineData);
      setOpportunities(oppData.opportunities || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  // nota direto na ficha: fica ligada só ao contato (sem atendimento)
  const handleAddNote = async (note) => {
    try {
      await api.post("/ticket-notes", { note, contactId: Number(contactId) });
      const { data } = await api.get(`/contacts/${contactId}/timeline`);
      setEvents(data);
      toast.success("Nota adicionada.");
    } catch (err) {
      toastError(err);
      return false;
    }
    return true;
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const templates = useContactFieldTemplates();
  const { fields: profileFields } = splitContactFields(contact?.extraInfo, templates);
  const customerSince = formatCustomerSince(contact);
  const birthday = getBirthdayInfo(contact?.birthDate);

  const handleCloseTicketModal = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  return (
    <MainContainer>
      <ContactModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          loadData();
        }}
        contactId={contactId ? Number(contactId) : null}
      />
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contact || {}}
        onClose={handleCloseTicketModal}
      />
      {contact && (
        <TaskEditor
          open={taskOpen}
          onClose={() => setTaskOpen(false)}
          initialContact={contact}
        />
      )}
      <OpportunityModal
        open={opportunityModalOpen}
        onClose={(saved) => {
          setOpportunityModalOpen(false);
          setEditingOpportunity(null);
          if (saved) loadData();
        }}
        opportunity={editingOpportunity}
        initialContact={contact}
      />
      <MainHeader>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <IconButton
            size="small"
            onClick={() => history.push("/contacts")}
            title="Voltar"
          >
            <ArrowBackIcon />
          </IconButton>
          <Title>Ficha do Contato</Title>
        </div>
        <MainHeaderButtonsWrapper />
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {loading ? (
          <div className={classes.centered}>
            <CircularProgress />
          </div>
        ) : !contact ? (
          <Typography color="textSecondary" style={{ padding: 16 }}>
            Contato não encontrado.
          </Typography>
        ) : (
          <>
            <Paper variant="outlined" className={classes.headerCard}>
              <ContactAvatar
                src={contact.profilePicUrl}
                name={contact.name}
                className={classes.avatar}
              />
              <div className={classes.info}>
                <Typography variant="h6">{contact.name}</Typography>
                {contact.number && (
                  <Typography variant="body2" color="textSecondary">
                    <Link href={`tel:${contact.number}`}>{formatPhone(contact.number)}</Link>
                  </Typography>
                )}
                {contact.email && (
                  <Typography variant="body2" color="textSecondary">
                    <Link href={`mailto:${contact.email}`}>{contact.email}</Link>
                  </Typography>
                )}
                {customerSince && (
                  <Typography variant="caption" color="textSecondary" component="div">
                    Cliente desde {customerSince}
                  </Typography>
                )}
                {birthday && (
                  <Typography variant="caption" color="textSecondary" component="div">
                    Aniversário: {birthday.label}
                    {birthday.age !== null && ` (${birthday.age} anos)`}
                  </Typography>
                )}
                <div style={{ marginTop: 6 }}>
                  <ContactTagsEditor
                    contactId={contact.id}
                    tags={contact.tags || EMPTY_TAGS}
                    onChange={(tags) => setContact((prev) => ({ ...prev, tags }))}
                  />
                </div>
                {birthdayBadge(birthday) && (
                  <Chip
                    size="small"
                    label={birthdayBadge(birthday)}
                    style={{
                      marginTop: 4,
                      height: 22,
                      fontWeight: 600,
                      backgroundColor: "#8E24AA33",
                      color: "#BA68C8",
                    }}
                  />
                )}
              </div>
              <div className={classes.actions}>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  className={classes.actionButton}
                  startIcon={<WhatsAppIcon />}
                  onClick={() => setNewTicketModalOpen(true)}
                >
                  Conversar
                </Button>
                <Button
                  variant="outlined"
                  className={`${classes.actionButton} ${classes.secondaryButton}`}
                  onClick={() => setModalOpen(true)}
                >
                  Editar
                </Button>
              </div>
            </Paper>

            <ContactAlerts contact={contact} style={{ marginBottom: 16 }} />

            {profileFields.length > 0 && (
              <Paper variant="outlined" className={classes.extraInfo}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Ficha
                </Typography>
                <Grid container spacing={2}>
                  {profileFields.map((info) => (
                    <Grid item xs={12} sm={6} md={4} key={info.id || info.name}>
                      <Typography variant="caption" color="textSecondary">
                        {info.name}
                      </Typography>
                      <Typography variant="body2">{info.value}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

            <Paper variant="outlined" className={classes.extraInfo}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 16,
                }}
              >
                <Typography variant="subtitle1" style={{ marginBottom: 0 }}>
                  Negócios
                </Typography>
                <Button
                  variant="outlined"
                  className={`${classes.actionButton} ${classes.secondaryButton} ${classes.smallActionButton}`}
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingOpportunity(null);
                    setOpportunityModalOpen(true);
                  }}
                >
                  Novo negócio
                </Button>
              </div>
              {opportunities.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  Nenhum negócio com este contato ainda.
                </Typography>
              ) : (
                <Grid container spacing={1}>
                  {opportunities.map((opportunity) => (
                    <Grid item xs={12} key={opportunity.id}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                          cursor: "pointer",
                          padding: "6px 0",
                        }}
                        onClick={() => {
                          setEditingOpportunity(opportunity);
                          setOpportunityModalOpen(true);
                        }}
                      >
                        <Chip
                          size="small"
                          label={opportunity.stage?.name}
                          style={{
                            backgroundColor: `${opportunity.stage?.color || "#9E9E9E"}33`,
                            color: opportunity.stage?.color,
                            fontWeight: 600,
                          }}
                        />
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {opportunity.title}
                        </Typography>
                        {opportunity.value != null && opportunity.value !== "" && (
                          <Typography variant="body2" color="textSecondary">
                            {Number(opportunity.value).toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </Typography>
                        )}
                        {opportunity.status === "lost" && opportunity.lostReason && (
                          <Typography variant="caption" color="textSecondary">
                            Motivo: {opportunity.lostReason}
                          </Typography>
                        )}
                      </div>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>

            <Paper variant="outlined" className={classes.timelinePaper}>
              <ContactTimeline
                events={events}
                onAddNote={handleAddNote}
                onAddTask={() => setTaskOpen(true)}
              />
            </Paper>
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default ContactProfile;
